/**
 * Main Layout - layout с bottom menu
 * Используется для основных страниц: home, wishlists, community, profile
 */

import { Suspense } from 'react';
import { Outlet, useLocation } from 'react-router';
import { BottomMenu } from '@/components/bottom-menu';
import { useAppActions } from '@/app/providers';
import { useCurrentUser } from '@/features/user';
import { ROUTE_PATHS } from '@/lib';
import { LoadingScreen, ScrollToTop } from '@/components';
import type { BottomMenuItemId } from '@/config';

/**
 * Определяет активный пункт меню на основе текущего пути
 */
function getActiveMenuItem(path: string, currentUserId?: string): BottomMenuItemId {
  if (path === ROUTE_PATHS.MY_WISHLISTS) return 'wishlist';
  // Все роуты вишлистов и желаний (WISHLIST_DETAIL, ITEM_DETAIL и т.д.) под общим префиксом
  if (path.startsWith(ROUTE_PATHS.WISHLIST_DETAIL.split(':')[0])) return 'wishlist';
  // Все желания текущего пользователя — раздел вишлистов
  if (path === ROUTE_PATHS.MY_ITEMS) return 'wishlist';
  if (path === ROUTE_PATHS.COMMUNITY) return 'community';
  // Профиль другого пользователя — часть раздела «Друзья/Community»,
  // но если это профиль текущего пользователя — подсвечиваем «profile»
  if (path.startsWith('/user/')) {
    if (currentUserId && path === `/user/${currentUserId}`) return 'profile';
    return 'community';
  }
  if (path === ROUTE_PATHS.PROFILE) return 'profile';
  
  // По умолчанию - home (включая все вкладки home)
  return 'home';
}

export function MainLayout(): JSX.Element {
  const location = useLocation();
  const { data: currentUser } = useCurrentUser();
  const activeMenuItem = getActiveMenuItem(location.pathname, currentUser?.id);
  const { wishlist, item, quickMenu, navigation } = useAppActions();

  return (
    <>
      {/* Основной контент */}
      <div className="h-screen flex flex-col bg-background">
        <div className="flex-1 overflow-y-auto" data-scroll-container>
          <ScrollToTop />
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </div>
      </div>

      {/* Нижнее меню - всегда видимо в MainLayout */}
      <BottomMenu 
        activeMenuItem={activeMenuItem}
        onMenuItemChange={navigation.handleMenuItemChange}
        onSelectWishlist={wishlist.createFromMenu}
        onSelectWish={item.createFromMenu}
        onFindFriend={quickMenu.findFriend}
        onSecretSanta={quickMenu.secretSanta}
      />

    </>
  );
}